import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { ButtonProps, Menu, MenuList } from '@mui/material';
import Button from '@mui/material/Button';
import ResultsExportDialog from 'components/ResultsExport/ResultsExportDialog';
import { useResultsExportDialogHost } from 'services/export/useResultsExportUrlState';
import { ReviewForm } from 'interfaces/reviewForm';
import { Study } from 'interfaces/study';
import React from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import SelectPrepareJobToExportModal from './SelectPrepareJobToExportModal';
import StudyExportMenuItems from './StudyExportMenuItems';

interface ExperimentalStudyExportMenuProps {
  children?: React.ReactNode;
  study?: Study;
  skipPreloadingStudyStains?: boolean; // If true, skip querying stain types in the study (i.e. when used in list of studies)
  disabled: boolean;
  forms?: ReviewForm[];
  triggerButtonColor?: ButtonProps['color'];
  triggerButtonVariant?: ButtonProps['variant'];
}

const ExperimentalStudyExportMenu: React.FunctionComponent<ExperimentalStudyExportMenuProps> = ({
  study,
  skipPreloadingStudyStains,
  disabled,
  forms,
  triggerButtonColor = 'secondary',
  triggerButtonVariant = 'contained',
}) => {
  const activeStudyId = study?.id;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const exportDialog = useResultsExportDialogHost(activeStudyId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuOpen = Boolean(anchorEl);

  const [selectPrepareJobToExportModalStudyId, setSelectPrepareJobToExportModalStudyId] = useQueryParam(
    'selectPrepareJobToExportModalStudyId',
    StringParam
  );
  const isSelectPrepareJobToExportModalOpen =
    Boolean(activeStudyId) && selectPrepareJobToExportModalStudyId === activeStudyId;

  return (
    <>
      <Button
        onClick={handleClick}
        color={triggerButtonColor}
        disabled={disabled}
        endIcon={menuOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        variant={triggerButtonVariant}
        size="small"
      >
        Export
      </Button>
      <Menu
        onClick={(event) => event.stopPropagation()}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleClose}
      >
        <MenuList>
          <StudyExportMenuItems
            study={study}
            disabled={disabled}
            onOpenExportData={() => {
              handleClose();
              exportDialog.open();
            }}
          />
        </MenuList>
      </Menu>
      {exportDialog.isOpen && (
        <ResultsExportDialog
          study={study}
          forms={forms}
          skipPreloadingStudyStains={skipPreloadingStudyStains}
          onClose={exportDialog.close}
        />
      )}

      {Boolean(study?.allowExport && study?.hasResults && isSelectPrepareJobToExportModalOpen) && (
        <SelectPrepareJobToExportModal
          open={isSelectPrepareJobToExportModalOpen}
          onClose={() => setSelectPrepareJobToExportModalStudyId(undefined)}
          studyId={activeStudyId}
        />
      )}
    </>
  );
};

export default ExperimentalStudyExportMenu;
